import { useCallback, useRef, useState } from 'react';

export function useDocumentDownload<T>(options: {
  fetchBlob: (id: string) => Promise<Blob>;
  getDocId: (doc: T) => string | undefined;
  getFilename: (doc: T) => string;
}) {
  const { fetchBlob, getDocId, getFilename } = options;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const downloadingRef = useRef(false);

  const downloadDocument = useCallback(
    async (doc: T) => {
      const id = getDocId(doc);
      if (!id || downloadingRef.current) return;
      downloadingRef.current = true;
      setDownloadingId(id);
      try {
        const blob = await fetchBlob(id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = getFilename(doc);
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      } finally {
        downloadingRef.current = false;
        setDownloadingId(null);
      }
    },
    [fetchBlob, getDocId, getFilename]
  );

  const handleDownloadClick = useCallback((e: React.MouseEvent, doc: T) => {
    e.stopPropagation();
    downloadDocument(doc);
  }, [downloadDocument]);

  return { downloadingId, downloadDocument, handleDownloadClick };
}
